/**
 * 팀 근태 문구 — 서버 `modules/hr/team-attendance.routes.ts` 와 같은 말을 쓴다 (근태 D4).
 */

import type { MemberState } from '../api/hr-team';
import { formatWorkMinutes } from './daily-work';

export const MEMBER_STATE_LABEL: Record<MemberState, string> = {
  /**
   * ★★ **「근무 중」이 아니라 「정상」이다** (2026-08-31 · 시안 E1).
   *    표의 «근무» 칸이 이미 「근무 중」이라 적는다 — 상태 칸까지 같은 말을 하면 한 줄에
   *    같은 낱말이 두 번 선다. 상태 칸이 답하는 물음은 「지금 무엇을 하나」가 아니라
   *    **「어긋난 것이 있나」**다: 정상 · 지각 · 미출근 · 휴가.
   * ⚠ 이 값은 **내려받는 파일도 쓴다**(서버 `MEMBER_STATE_TEXT`) — 한쪽만 고치면
   *   받아 간 사람이 화면과 못 맞춘다. 서버와 **같이** 고쳤다.
   */
  WORKING: '정상',
  OFF: '퇴근',
  LEAVE: '휴가',
  DEEMED: '간주 출근',
  ABSENT: '미출근',
};

/** 상태 → 색 계열. CSS 는 `is-<tone>` 으로 받는다(이모지 금지 — 점 색과 라벨로만 구분). */
export const MEMBER_STATE_TONE: Record<MemberState, 'active' | 'off' | 'away' | 'idle'> = {
  WORKING: 'active',
  OFF: 'off',
  // 휴가와 간주(출장·외근)는 둘 다 "자리에 없음" 계열 — 구분은 라벨이 한다.
  LEAVE: 'away',
  DEEMED: 'away',
  ABSENT: 'idle',
};

/**
 * `4시간 51분` · `51분`.
 *
 * ★ 표기는 **`lib/daily-work.ts` 의 `formatWorkMinutes` 한 곳**을 쓴다 (2026-09-26).
 *   견본 22개에서 **글자가 전부 같았다** — 옮겨도 보이는 글자가 안 바뀐다(회귀 0).
 */
export function formatSpan(minutes: number): string {
  return formatWorkMinutes(minutes);
}

/**
 * 요약 칸 → 표 거르기 (2026-08-30 · 판 F).
 *
 * ★★ **숫자를 보고 눌렀는데 목록이 안 걸리면 다시 눈으로 찾게 된다.** 이상 근태에서 쓴 것과
 *    같은 규칙이다 — 칸이 곧 필터다.
 * ★ **「퇴근 누락」은 여기 없다.** 그 칸은 **어제 이전 누계**라 **오늘 표에서 거를 수가 없다** —
 *   거르는 시늉을 하느니 **이상 근태 화면으로 보내는 것**이 정직하다.
 */
/**
 * ★ `WORKING_NOW` · `TRIP` 은 2026-09-06(달력 판)에 보탰다 — 요약 다섯 칸이 **겹치지 않게**
 *   하려면 「근무 중(출장 제외)」과 「출장」을 갈라야 한다. 옛 값들은 **한 글자도 안 바꿨다**.
 */
export type AttendanceStateFilter =
  | 'PRESENT' | 'WORKING' | 'WORKING_NOW' | 'TRIP' | 'OFF' | 'LEAVE' | 'ABSENT' | 'LATE';

/** 그 칸이 고르는 줄인가. `late` 는 상태가 아니라 «오늘 늦었나» 라 따로 본다. */
export function matchesStateFilter(
  row: { state: MemberState; late: boolean },
  f: AttendanceStateFilter | null,
): boolean {
  if (!f) return true;
  switch (f) {
    // 「나온 사람」 — 찍고 일하는 중 · 퇴근함 · 출장·외근. 서버의 `present` 와 같은 셈이다.
    case 'PRESENT': return row.state === 'WORKING' || row.state === 'OFF' || row.state === 'DEEMED';
    case 'WORKING': return row.state === 'WORKING' || row.state === 'DEEMED';
    // 찍고 «아직 일하는 중» 만 — 출장(간주)은 아래 TRIP 이 맡는다.
    case 'WORKING_NOW': return row.state === 'WORKING';
    case 'TRIP': return row.state === 'DEEMED';
    case 'OFF': return row.state === 'OFF';
    case 'LEAVE': return row.state === 'LEAVE';
    case 'ABSENT': return row.state === 'ABSENT';
    case 'LATE': return row.late;
    default: return true;
  }
}

/** 눌린 칸이 무엇인지 화면이 말할 때 쓰는 말. */
export const STATE_FILTER_LABEL: Record<AttendanceStateFilter, string> = {
  PRESENT: '출근', WORKING: '근무 중', WORKING_NOW: '근무 중', TRIP: '출장 · 외근',
  OFF: '퇴근', LEAVE: '휴가', ABSENT: '미출근', LATE: '지각',
};
